// Helpers tipados del MÓDULO 2 (Radicación de la queja + Reparto).
// Centraliza las llamadas al backend para que las vistas no repitan rutas/DTOs.
// Base de la API y token JWT los resuelve `api` (lib/api.ts). Todo requiere Bearer.

import { api } from './api';
import type { DocumentoBorrador } from './decisionFlujo';

// ---- Catálogos (enums del backend) ----------------------------------------
export interface Catalogos {
  tipoDocumento: string[];
  rol: string[];
  modulo: string[];
  tipoIngresoQueja: string[];
  modalidadConducta: string[];
  tipoImplicado: string[];
  calidadImplicado: string[];
  autoridadSegundaInstancia: string[];
}

// ---- Tipos del dominio (respuestas snake_case del backend) ----------------
export interface InvestigadoResumen {
  nombre?: string | null;
  tipo: 'DETERMINADO' | 'POR_ESTABLECER';
  tipo_documento?: string | null;
  numero_documento?: string | null;
  cargo?: string | null;
  entidad_nombre?: string | null;
  dependencia?: string | null;
  calidad?: string | null;
}

export interface Expediente {
  id: string;
  radicado: string;
  radicado_manual?: string | null;
  fecha_queja: string;
  tipo_ingreso: string;
  estado_actual: string;
  etapa_actual?: string | null;
  modalidad_conducta?: string | null;
  fecha_prescripcion?: string | null;
  prioridad?: string | null;
  funcionario_actual?: string | null;
  fecha_hechos_tentativa?: boolean; // true si la fecha de los hechos es tentativa (falta la real)
  decision_estado?: string | null; // estado del trámite de decisión inicial (para la "Situación")
  decision_motivo?: string | null; // motivo de la última devolución (o comentarios del director)
  investigados?: InvestigadoResumen[];
  apoderados?: ApoderadoResumen[];
}

// Apoderado a nivel de expediente (p. ej. capturado al radicar).
export interface ApoderadoResumen {
  nombre?: string | null;
  numero_documento?: string | null;
  tarjeta_profesional?: string | null;
  correo?: string | null;
  direccion_notificacion?: string | null;
}

// Etiqueta visible del tipo de implicado. El enum de BD no cambia; solo el texto:
// POR_ESTABLECER se muestra como "Individualizar".
export function labelTipoImplicado(v?: string | null): string {
  switch (v) {
    case 'DETERMINADO': return 'Determinado';
    case 'POR_ESTABLECER': return 'Individualizar';
    default: return v ?? '—';
  }
}

// Etiqueta visible de «¿Quién interpone la notificación disciplinaria?».
// El enum de BD no cambia; solo el texto (en español y con tildes).
export function labelTipoIngreso(v?: string | null): string {
  switch (v) {
    case 'DE_OFICIO': return 'De oficio';
    case 'QUEJOSO': return 'Quejoso';
    case 'INFORME': return 'Informe';
    case 'ANONIMO': return 'Anónimo';
    default: return v ?? '—';
  }
}

export interface Quejoso {
  nombre?: string | null;
  tipo_documento?: string | null;
  numero_documento?: string | null;
  direccion?: string | null;
  municipio?: string | null;
  departamento?: string | null;
  telefono?: string | null;
  correo?: string | null;
  procedencia?: string | null; // solo ANÓNIMO
}

export interface Hechos {
  id?: string;
  fecha_hechos?: string | null;
  fecha_hechos_tentativa?: boolean;
  lugar?: string | null;
  descripcion?: string | null;
  dependencia_involucrada?: string | null;
  observaciones?: string | null;
}

export interface Reparto {
  id?: string;
  funcionario?: string | null;
  motivo?: string | null;
  termino_interno?: string | null;
  prioridad?: string | null;
  observaciones?: string | null;
  fecha?: string | null;
}

// Decisión inicial (doble visto bueno) embebida en el detalle del expediente.
export interface DecisionInicialResumen {
  id: string;
  tipo?: string | null;
  estado: string;
  observaciones?: string | null;
  motivo_devolucion?: string | null;
  comentarios_director?: string | null;
  documento_datos?: DocumentoBorrador | null; // borrador persistido de la decisión
  // Notificación al personal (Sí/No + constancia) y revisores asignados.
  notificado?: boolean | null;
  notificacion_constancia_key?: string | null;
  revisor_abogado_id?: string | null;
  revisor_supervisor_id?: string | null;
  // Firmantes del flujo (para estampar sus firmas en el documento): quién propuso,
  // quién dio el visto bueno (abogado/supervisor) y quién aprobó (director).
  propuesta_por?: string | null;
  visto_bueno_por?: string | null;
  aprobada_por?: string | null;
  fecha_propuesta?: string | null;
  fecha_revision?: string | null;
}

/**
 * ACTA DE REPARTO (Etapa 1) que originó el expediente: queda relacionada a él,
 * así que al consultar el expediente se ve de dónde vino el caso, quién lo
 * firmó y cuándo. Es null en expedientes radicados sin pre-radicación (legado).
 */
/** Soporte cargado del comité (acta, PDF de la decisión, constancia). */
export interface SoporteComite {
  id: string;
  categoria: 'COMITE_ACTA' | 'DECISION_COMITE' | 'COMITE_CONSTANCIA';
  nombre: string;
  storage_key: string;
}

/**
 * Respuesta del COMITÉ DE QUEJA, ligada al expediente para verla al consultarlo.
 * Es null cuando el caso se fue por Reparto directo (sin comité).
 */
export interface RespuestaComite {
  fecha_comite?: string | null;
  reprogramado?: boolean;
  fecha_comite_nueva?: string | null;
  decision?: string | null;
  fecha_decision?: string | null;
  justificacion?: string | null;
  remision_destino?: string | null; // Remisión por competencia: a quién se envía
  otro_detalle?: string | null; // Otro: «indique cuál»
  soportes?: SoporteComite[];
}

export interface ActaRepartoResumen {
  solicitud_id: string;
  comite?: RespuestaComite | null;
  radicado_origen?: string | null;
  canal?: string | null;
  ruta?: string | null;
  fecha_ingreso?: string | null;
  acta_estado?: string | null;
  acta_key?: string | null;
  acta_visto_bueno_en?: string | null;
  acta_firmada_en?: string | null;
  fecha_reparto?: string | null;
  fecha_asignacion?: string | null;
  plazo_abogado_fecha?: string | null;
  director_nombre?: string | null;
  sustanciador_nombre?: string | null;
  abogado_nombre?: string | null;
  tecnico_nombre?: string | null;
}

// Detalle completo (GET /expedientes/:id).
export interface ExpedienteDetalle extends Expediente {
  quejosos?: Quejoso[];
  hechos?: Hechos[] | null; // el backend devuelve un array (relación 1-N)
  repartos?: Reparto[];
  decision_inicial?: DecisionInicialResumen | null;
  acta_reparto?: ActaRepartoResumen | null;
}

export interface Pagina<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ---- DTOs de entrada (camelCase, como espera el backend) ------------------
export interface QuejosoInput {
  nombre?: string; tipoDocumento?: string; numeroDocumento?: string;
  direccion?: string; municipio?: string; departamento?: string;
  telefono?: string; correo?: string;
  procedencia?: string; // solo ANÓNIMO: de dónde proviene la queja
}

export interface HechosInput {
  fechaHechos?: string; esTentativa?: boolean; lugar?: string; descripcion: string;
  dependenciaInvolucrada?: string; observaciones?: string;
}

export interface InvestigadoInput {
  tipo: 'DETERMINADO' | 'POR_ESTABLECER';
  nombre?: string; tipoDocumento?: string; numeroDocumento?: string;
  cargo?: string; entidadNombre?: string; dependencia?: string; calidad?: string;
}

export interface ApoderadoInput {
  nombre: string;
  numeroDocumento?: string;
  tarjetaProfesional?: string;
  correo?: string;
  direccionNotificacion?: string;
}

export interface RadicarInput {
  tipoIngreso: 'DE_OFICIO' | 'QUEJOSO' | 'INFORME' | 'ANONIMO';
  fechaQueja: string;
  radicadoManual?: string;
  modalidadConducta?: 'INSTANTANEA' | 'PERMANENTE' | 'CONTINUADA' | 'OMISIVA';
  fechaBasePrescripcion?: string;
  prioridad?: string;
  quejoso?: QuejosoInput;
  hechos?: HechosInput;
  apoderado?: ApoderadoInput;
  investigados: InvestigadoInput[];
}

export interface CargaLaboral {
  filas: { funcionarioId: string; funcionario: string; total: number }[];
  alerta: string | null;
}

// Usuario del Módulo 1 (para elegir funcionario en el reparto).
export interface UsuarioLite {
  id: string;
  nombre: string;
  apellido: string;
  rol: string;
}

// ---- Llamadas -------------------------------------------------------------

/** Catálogos (enums) para poblar los selects. */
export function getCatalogos(): Promise<Catalogos> {
  return api<Catalogos>('/catalogos');
}

/** Radicar una queja. Devuelve el expediente creado (con radicado y prescripción). */
export function radicar(body: RadicarInput): Promise<Expediente> {
  return api<Expediente>('/expedientes', { method: 'POST', body });
}

/** Listado paginado de expedientes con filtros opcionales. */
export function listarExpedientes(params: {
  estado?: string; funcionarioId?: string; participanteId?: string; page?: number; limit?: number;
} = {}): Promise<Pagina<Expediente>> {
  const qs = new URLSearchParams();
  if (params.estado) qs.set('estado', params.estado);
  if (params.funcionarioId) qs.set('funcionarioId', params.funcionarioId);
  if (params.participanteId) qs.set('participanteId', params.participanteId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return api<Pagina<Expediente>>(`/expedientes${s ? `?${s}` : ''}`);
}

/** Detalle completo de un expediente. */
export function getExpediente(id: string): Promise<ExpedienteDetalle> {
  return api<ExpedienteDetalle>(`/expedientes/${id}`);
}

/** Fija la fecha REAL de los hechos (reemplaza la tentativa) y recalcula prescripción. */
export function fijarFechaHechos(id: string, fechaHechos: string): Promise<ExpedienteDetalle> {
  return api<ExpedienteDetalle>(`/expedientes/${id}/fecha-hechos`, { method: 'PATCH', body: { fechaHechos } });
}

/** Edita los datos de la Etapa 2 (solo si el especialista devolvió el caso a corregir). */
export function editarEtapa2(id: string, body: RadicarInput): Promise<ExpedienteDetalle> {
  return api<ExpedienteDetalle>(`/expedientes/${id}/etapa2`, { method: 'PATCH', body });
}

/** Coincidencia de un documento con expedientes existentes (aviso de duplicado en Etapa 2). */
export interface ExpedienteDeDocumento {
  expedienteId: string;
  radicado: string;
  radicadoManual?: string | null;
  estadoActual: string;
  nombre?: string | null;
}
export interface BusquedaPorDocumento {
  documento: string;
  existe: boolean;
  expedientes: ExpedienteDeDocumento[];
}

/** Verifica si un número de documento ya figura como investigado en algún expediente. */
export function buscarExpedientesPorDocumento(doc: string): Promise<BusquedaPorDocumento> {
  return api<BusquedaPorDocumento>(`/expedientes/por-documento?doc=${encodeURIComponent(doc)}`);
}

/** Asignar reparto (solo Director). */
export function asignarReparto(id: string, body: {
  funcionarioId: string; terminoInterno?: string; prioridad?: string; observaciones?: string;
}): Promise<Expediente> {
  return api<Expediente>(`/expedientes/${id}/reparto`, { method: 'POST', body });
}

/** Reasignar reparto (motivo obligatorio). */
export function reasignarReparto(id: string, body: {
  funcionarioId: string; motivo: string; terminoInterno?: string; prioridad?: string; observaciones?: string;
}): Promise<Expediente> {
  return api<Expediente>(`/expedientes/${id}/reparto/reasignar`, { method: 'POST', body });
}

/** Carga laboral por funcionario (+ alerta de desbalance). */
export function cargaLaboral(): Promise<CargaLaboral> {
  return api<CargaLaboral>('/reparto/carga-laboral');
}

// Roles de instrucción a los que se puede repartir un expediente.
export const ROLES_INSTRUCCION = ['FUNCIONARIO_INSTRUCCION', 'CONTRATISTA_INSTRUCCION'];

/** Usuarios candidatos a reparto (roles de instrucción). Filtra en el cliente. */
export async function funcionariosInstruccion(): Promise<UsuarioLite[]> {
  const usuarios = await api<UsuarioLite[]>('/usuarios');
  return usuarios.filter((u) => ROLES_INSTRUCCION.includes(u.rol));
}
